import {
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  TAB_INDICATOR_TRANSITION,
  placeTabIndicator,
  watchTabIndicators,
} from '../../lib/tabIndicator'

/**
 * The sliding highlight, as a React shell.
 *
 * Every tab-like control in the product is one of these: the segmented
 * controls, the filter chip bars, the settings rail. (The top nav is the same
 * behaviour driven from ERB — see lib/appNav.ts — because it has to survive a
 * Turbo body swap; both sit on lib/tabIndicator.ts.)
 *
 * The group owns nothing but the indicator. Options are the caller's, so a
 * rail option can be two stacked lines and a filter chip can carry a count,
 * without this component growing a variant for each. What it does insist on is
 * that an option paints *no background of its own* — only colour and weight
 * change — because two things painting the selected state is exactly what the
 * sliding highlight replaced.
 */

export type TabLook = 'segment' | 'chip' | 'rail' | 'nav'

/**
 * Indicator looks, from 00-foundation.md §5.1. Radius is a separate prop
 * because the segmented control uses 6px inside a 9px shell and 8px inside an
 * 11px one, and both are in the prototype.
 */
const LOOKS: Record<TabLook, CSSProperties> = {
  segment: {
    background: 'var(--w-pill-fill)',
    border: '1px solid var(--w-pill-line)',
  },
  chip: {
    background: 'var(--w-pill-fill)',
    border: '1px solid var(--w-pill-line)',
  },
  rail: {
    background: 'var(--w-pane-hi)',
    border: '1px solid var(--w-attention-line)',
  },
  nav: {
    background: 'var(--w-fill)',
    boxShadow: 'var(--w-inset)',
  },
}

const DEFAULT_RADIUS: Record<TabLook, number> = { segment: 6, chip: 7, rail: 10, nav: 8 }

interface TabGroupProps {
  look: TabLook
  /**
   * Whatever identifies the selected option. Not read — it is the signal to
   * re-measure, so passing the key is enough and passing an object that is
   * rebuilt every render is harmless but pointless.
   */
  active: string | number | null
  radius?: number
  children: ReactNode
  className?: string
  style?: CSSProperties
  /** Falls through to the group element, e.g. role="tablist". */
  role?: string
  'aria-label'?: string
}

export function TabGroup({
  look,
  active,
  radius,
  children,
  className = '',
  style,
  role,
  'aria-label': ariaLabel,
}: TabGroupProps) {
  const group = useRef<HTMLDivElement>(null)

  // Layout effect, not effect: the indicator has to be over the right option in
  // the frame the group first paints, or it fades in at the corner and slides
  // across on load. placeTabIndicator's own first-placement handling covers the
  // transition; this covers the frame.
  useLayoutEffect(() => {
    const el = group.current
    if (el) placeTabIndicator(el)
  }, [active, children])

  useEffect(() => {
    const el = group.current
    if (!el) return

    const place = () => placeTabIndicator(el)
    const stop = watchTabIndicators(place)

    // An option's box can change without the selection changing: the settings
    // rail's notes are live ("6 of 9 on"), and a filter chip carries a count.
    const observer = new ResizeObserver(place)
    observer.observe(el)
    Array.from(el.children).forEach((child) => observer.observe(child))

    return () => {
      stop()
      observer.disconnect()
    }
  }, [])

  return (
    <div
      ref={group}
      data-tabs
      role={role}
      aria-label={ariaLabel}
      className={className}
      style={{ position: 'relative', ...style }}
    >
      <span
        data-tab-ind
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          borderRadius: radius ?? DEFAULT_RADIUS[look],
          opacity: 0,
          boxSizing: 'border-box',
          pointerEvents: 'none',
          transition: TAB_INDICATOR_TRANSITION,
          ...LOOKS[look],
        }}
      />
      {children}
    </div>
  )
}

interface TabOptionProps {
  on: boolean
  onClick: () => void
  children: ReactNode
  className?: string
  style?: CSSProperties
  title?: string
}

/**
 * One option. `position:relative` so it stacks above the indicator, and a
 * colour transition matched to the slide so the label arriving at full strength
 * and the highlight arriving under it read as one movement.
 *
 * A button rather than a span: these are pressable, and every one of them was a
 * div in the prototype, which is a prototype's privilege and not ours.
 */
export function TabOption({ on, onClick, children, className = '', style, title }: TabOptionProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      title={title}
      data-tab-on={on ? '1' : '0'}
      onClick={onClick}
      className={`tab ${className}`}
      style={{
        position: 'relative',
        appearance: 'none',
        background: 'none',
        border: 0,
        margin: 0,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'color .22s ease',
        ...style,
      }}
    >
      {children}
    </button>
  )
}
